import { AppNavParams } from '@euler/app/Routes';
import {
  ListMask,
  useScrollableMask,
} from '@euler/app/components/list/ListMask';
import {
  ScoreGaugeView,
  useDefaultScoreGaugeStyle,
} from '@euler/app/components/score/ScoreGauge';
import { calculateScoreViewDimension } from '@euler/app/components/score/util';
import { Typography } from '@euler/app/components/typography';
import { ReportRef } from '@euler/app/flows/report/functions';
import { reportScreenFromTabKey } from '@euler/app/flows/report/functions/util';
import { Center } from '@euler/components/Center';
import { Colors } from '@euler/components/Colors';
import { HStack } from '@euler/components/layout/Stack';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useIsLandscape } from '@euler/functions/responsiveness';
import { useShareReport } from '@euler/functions/sharing/useShareReport';
import { useContainerDimensions } from '@euler/functions/useAdaptiveContainerWidth';
import { DeliveryCheckSceneType } from '@euler/model/enum';
import { VehicleReport } from '@euler/model/report';
import { ReportTabKey } from '@euler/model/viewmodel';
import { formatDateTime } from '@euler/utils/formatters';
import { usePersistFn } from '@euler/utils/hooks';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { memo, useCallback, useMemo } from 'react';
import {
  FlatList,
  Platform,
  ScrollView,
  StyleProp,
  TouchableOpacity,
  View,
  ViewStyle,
} from 'react-native';
import { Fill } from 'react-slot-fill';
import { useDeliveryCheckReportModels } from '../../functions/useDeliveryCheckReportModels';
import { DeliveryCheckCell } from './DeliveryCheckCell';

const kCellMargin = 20;
const kSideFlex = 0.45;

export const DeliveryCheckReportViewForIpad = memo(
  ({
    report,
    sceneType,
    reportTarget,
    standalone,
  }: {
    report: VehicleReport;
    sceneType: DeliveryCheckSceneType;
    reportTarget: ReportRef;
    standalone?: boolean;
  }) => {
    const { width } = useContainerDimensions();
    const { rpx } = useResponsiveContext();
    const navigation = useNavigation<StackNavigationProp<AppNavParams>>();

    const sideWidth = Math.round(width * kSideFlex) - 2 * kCellMargin;
    const cellWidth = Math.round(width * (1 - kSideFlex)) - 2 * kCellMargin;

    const { cells } = useDeliveryCheckReportModels(report, sceneType);

    const { shareReportViaWeixin } = useShareReport();

    const {
      onScroll,
      onLayout,
      onContentSizeChange,
      showLeadingMask,
      showTrailingMask,
    } = useScrollableMask({ orientation: 'vertical' });

    const onViewReport = usePersistFn((tab: ReportTabKey) => {
      navigation.navigate(reportScreenFromTabKey(tab), {
        target: reportTarget,
        standalone: true,
        onShare: () => {
          void shareReportViaWeixin({
            target: reportTarget,
            orderNo: reportTarget.orderNo,
            reportNo: reportTarget.reportNo,
            licensePlateNo: reportTarget.licensePlateNo,
            tab,
          });
        },
      });
    });

    return (
      <>
        <HStack
          alignItems="stretch"
          css={`
            flex: 1;
          `}
        >
          <ScrollView
            contentContainerStyle={{
              paddingBottom: rpx(44),
            }}
            css={`
              flex: ${10 * kSideFlex};
            `}
          >
            <SummaryView
              report={report}
              width={sideWidth}
              onViewReport={standalone ? onViewReport : undefined}
              css={`
                margin-top: ${rpx(16)}px;
              `}
            />
          </ScrollView>
          <ListMask
            leadingMask={showLeadingMask}
            trailingMask={showTrailingMask}
            location={0.05}
            orientation="vertical"
            css={`
              flex: ${10 * (1 - kSideFlex)};
              margin-left: ${rpx(kCellMargin)}px;
            `}
          >
            <FlatList
              data={cells}
              keyExtractor={x => String(x.index)}
              showsVerticalScrollIndicator={Platform.OS === 'web'}
              onScroll={onScroll}
              onLayout={onLayout}
              onContentSizeChange={onContentSizeChange}
              renderItem={({ item }) => (
                <DeliveryCheckCell
                  model={item}
                  width={cellWidth}
                  hideInfo
                  mediaListContainerStyle={{ borderRadius: 0 }}
                  css={`
                    border-radius: ${rpx(20)}px;
                  `}
                />
              )}
            />
          </ListMask>
        </HStack>
        <Fill name="reports/deliveryCheck/page-container/footer/left">
          <Typography.Label light color={Colors.White5} size={rpx(18)}>
            质检人员:{' '}
            {report.deliveryCheck?.technicianName ?? report.serviceAgentName}
          </Typography.Label>
          <Typography.Label light color={Colors.White5} size={rpx(18)}>
            质检时间:{' '}
            {formatDateTime(report.deliveryCheck?.createdAt ?? new Date())}
          </Typography.Label>
        </Fill>
      </>
    );
  },
);

const ScoreView = memo(
  (props: {
    score: number;
    width: number;
    height: number;
    style?: StyleProp<ViewStyle>;
  }) => {
    const { rpx } = useResponsiveContext();
    const dimens = calculateScoreViewDimension(props.width, props.height);
    const width = Math.round(dimens.width);
    const height =
      Math.round(dimens.height) * (width / Math.round(dimens.width));
    const scaleFactor = width / rpx(370);
    const defaultTheme = useDefaultScoreGaugeStyle();
    const theme = useMemo(() => {
      return {
        ...defaultTheme,
        scoreLabelColor: 'rgba(255, 255, 255, 0.75)',
        tickLabelColor: 'rgba(255, 255, 255, 0.5)',
        tickLabelFontSize: rpx(14),
        scoreLabelFontSize: 24 * scaleFactor,
        scoreFontSize: 72 * scaleFactor,
      };
    }, [defaultTheme, rpx, scaleFactor]);

    return (
      <Center
        css={`
          height: ${width}px;
        `}
      >
        <ScoreGaugeView
          score={props.score}
          width={width}
          height={height * (Math.min(width, rpx(460)) / width)}
          theme={theme}
          label="整车评分"
          scoreTextContainerStyle={{
            transform: [{ translateY: rpx(10) }],
          }}
          style={props.style}
        />
      </Center>
    );
  },
);

type SummaryItem = {
  type: ReportTabKey;
  title: string;
  finished: boolean;
};

const SummaryCell = memo(
  ({
    type,
    title,
    finished,
    style,
    onViewReport,
  }: {
    type: ReportTabKey;
    title: string;
    finished: boolean;
    style?: StyleProp<ViewStyle>;
    onViewReport?: (type: ReportTabKey) => void;
  }) => {
    const { rpx } = useResponsiveContext();

    let statusText = '已完成';
    if (!finished) {
      statusText =
        type === 'preInspection' || type === 'inspection'
          ? '未检测'
          : type === 'construction'
          ? '未施工'
          : '未开始';
    }

    const onPress = useCallback(() => {
      onViewReport?.(type);
    }, [onViewReport, type]);

    return (
      <View
        css={`
          margin-bottom: ${rpx(10)}px;
        `}
        style={style}
      >
        <View
          css={`
            border-bottom-width: 1px;
            border-bottom-color: rgba(255, 255, 255, 0.2);
          `}
        >
          <Typography.Label
            medium
            color={Colors.White7_5}
            size={rpx(20)}
            lineHeight={rpx(44)}
          >
            {title}
          </Typography.Label>
        </View>
        <HStack
          css={`
            margin-top: ${rpx(6)}px;
          `}
        >
          <Typography.Label light color={Colors.White5_5} size={rpx(15)}>
            {statusText}
          </Typography.Label>
          {onViewReport ? (
            <TouchableOpacity
              onPress={onPress}
              disabled={!finished}
              css={`
                border-radius: ${rpx(3)}px;
                border: 1px solid ${Colors.White5_5};
                padding: 0 ${rpx(7)}px;
              `}
            >
              <Typography.Label light color={Colors.White5_5} size={rpx(14)}>
                查看报告
              </Typography.Label>
            </TouchableOpacity>
          ) : null}
        </HStack>
      </View>
    );
  },
);

const SummaryView = memo(
  ({
    report,
    width,
    style,
    onViewReport,
  }: {
    report: VehicleReport;
    width: number;
    onViewReport?: (type: ReportTabKey) => void;
    style?: StyleProp<ViewStyle>;
  }) => {
    const { rpx } = useResponsiveContext();

    const cells = useMemo<SummaryItem[]>(() => {
      return [
        {
          type: 'preInspection',
          title: '接车预检',
          finished: report.status.preInspection,
        },
        {
          type: 'inspection',
          title: '全车检测',
          finished: report.status.inspection,
        },
        {
          type: 'construction',
          title: '施工反馈',
          finished: report.status.construction,
        },
      ];
    }, [
      report.status.construction,
      report.status.inspection,
      report.status.preInspection,
    ]);

    const isLandscape = useIsLandscape();
    const scoreWidth = isLandscape ? Math.min(rpx(300), width) : width;

    return (
      <View
        css={`
          margin: 0 ${rpx(10)}px;
        `}
        style={style}
      >
        <View
          css={`
            background-color: ${Colors.White0_5};
            border-top-width: ${rpx(3)}px;
            border-top-color: ${Colors.White3_5};
            border-bottom-width: ${rpx(3)}px;
            border-bottom-color: ${Colors.White3_5};
            padding: ${rpx(5)}px 0;
            margin-bottom: ${rpx(16)}px;
          `}
        >
          <Typography.Label
            bold
            color={Colors.White}
            size={rpx(25)}
            css={`
              text-align: center;
            `}
          >
            服务概要
          </Typography.Label>
        </View>
        <ScoreView
          score={report.score ?? 100}
          width={scoreWidth}
          height={scoreWidth * 0.8}
        />
        <View
          css={`
            margin-top: ${rpx(16)}px;
          `}
        >
          {cells.map(cell => (
            <SummaryCell
              key={cell.type}
              title={cell.title}
              type={cell.type}
              finished={cell.finished}
              onViewReport={onViewReport}
            />
          ))}
        </View>
        <View
          css={`
            margin-top: ${rpx(16)}px;
            padding: ${rpx(16)}px;
            border-width: 1px;
            border-color: ${Colors.White3_5};
            border-radius: ${rpx(20)}px;
            background-color: ${Colors.White0_5};
          `}
        >
          <Typography.Label thin size={14} color={Colors.White}>
            温馨提示: 完工交车后将无法修改服务记录, 如遇特殊情况,
            请联系我们协助您解决。
          </Typography.Label>
        </View>
      </View>
    );
  },
);
